import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../auth/context";
import { SharedView } from "./SharedView";
import {
  decideShare,
  fetchShareSnapshot,
  listShares,
  revokeShare,
  shareErrorMessage,
  type ShareRow,
} from "../shares/api";
import { ActivityWatchToggle } from "../shares/ActivityWatchToggle";

// /shared-with-me — list of accepted incoming user-targeted shares.
// /shared-with-me/:id — opens that share's snapshot via the same
// SharedView component used for anonymous /shared/:token URLs.

export function SharedWithMeList() {
  const { state } = useAuth();
  const accessToken =
    (state.status === "authenticated" && state.session.access_token) ||
    (state.status === "offline" && state.session.access_token) ||
    null;

  const [accepted, setAccepted] = useState<ShareRow[]>([]);
  const [pending, setPending] = useState<ShareRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const all = await listShares(accessToken, "incoming");
      setAccepted(all.filter((s) => s.acceptedAt));
      setPending(all.filter((s) => !s.acceptedAt));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!accessToken) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-10 text-sm text-slate-500 dark:text-slate-400">
        Sign in to see shares others have offered you.
      </main>
    );
  }

  const decide = async (id: string, action: "accept" | "decline") => {
    try {
      await decideShare(accessToken, id, action);
      await refresh();
    } catch (err) {
      setError(
        shareErrorMessage(err instanceof Error ? err.message : String(err)),
      );
    }
  };

  const leave = async (id: string) => {
    if (!confirm("Leave this share? You can ask the owner to re-share later.")) return;
    try {
      await revokeShare(accessToken, id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <main className="mx-auto max-w-2xl px-6 py-8">
      <div className="mb-4 flex items-baseline justify-between">
        <h1 className="text-xl font-semibold tracking-tight">Shared with me</h1>
        <Link to="/" className="text-sm text-slate-500 underline hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">
          Back
        </Link>
      </div>

      {error && (
        <p className="mb-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-300">
          {error}
        </p>
      )}

      {pending.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Pending offers
          </h2>
          <ul className="space-y-1.5">
            {pending.map((s) => (
              <li
                key={s.id}
                className="flex items-start gap-3 rounded border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 dark:text-slate-100">
                    {s.scopeTitle ?? `Untitled ${s.scope}`}
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400">
                    from {ownerLabel(s)} · {s.permissions}
                    {s.includeSubtree && s.scope === "item" ? " · with subtree" : ""}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => void decide(s.id, "accept")}
                    className="rounded bg-slate-900 px-2 py-0.5 text-xs font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    onClick={() => void decide(s.id, "decline")}
                    className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Decline
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Accepted
          {accepted.length > 0 && (
            <span className="ml-2 text-slate-400 dark:text-slate-500">{accepted.length}</span>
          )}
        </h2>
        {accepted.length === 0 ? (
          <p className="text-sm italic text-slate-400 dark:text-slate-500">
            {loading ? "Loading…" : "Nothing shared with you yet."}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {accepted.map((s) => (
              <li
                key={s.id}
                className="group flex items-center gap-3 rounded border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
              >
                <Link
                  to={`/shared-with-me/${s.id}`}
                  className="flex-1 min-w-0"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {s.scopeTitle ?? `Untitled ${s.scope}`}
                    </span>
                    {s.permissions === "edit" && (
                      <span className="rounded bg-emerald-100 px-1 text-[10px] font-medium text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                        editable
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400">
                    from {ownerLabel(s)} · {s.permissions}
                  </div>
                </Link>
                <button
                  type="button"
                  onClick={() => void leave(s.id)}
                  className="shrink-0 rounded px-1.5 py-0.5 text-xs text-rose-600 opacity-0 hover:bg-rose-50 hover:text-rose-700 group-hover:opacity-100 dark:text-rose-400 dark:hover:bg-rose-950 dark:hover:text-rose-300"
                >
                  Leave
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

export function SharedWithMeViewer() {
  const { id } = useParams<{ id: string }>();
  const { state } = useAuth();
  const accessToken =
    (state.status === "authenticated" && state.session.access_token) ||
    (state.status === "offline" && state.session.access_token) ||
    null;
  const [share, setShare] = useState<ShareRow | null>(null);

  useEffect(() => {
    if (!accessToken || !id) return;
    let alive = true;
    (async () => {
      try {
        const all = await listShares(accessToken, "incoming");
        if (alive) setShare(all.find((s) => s.id === id) ?? null);
      } catch {
        /* the loader below will surface the error */
      }
    })();
    return () => {
      alive = false;
    };
  }, [accessToken, id]);

  if (!accessToken || !id) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-10 text-sm text-slate-500 dark:text-slate-400">
        Sign in to open this share.
      </main>
    );
  }

  // Excalidraw scenes aren't tree/Gantt snapshots — SharedView can't render
  // them (the snapshot endpoint returns an empty tree for scope=excalidraw).
  // Point the recipient to their Excalidraw list instead, where the synced
  // scene is fully viewable/editable and live.
  if (share?.scope === "excalidraw") {
    return <ExcalidrawShareNotice share={share} />;
  }

  // SharedView's loader-prop path lets us reuse its tree/Gantt rendering
  // with an auth'd fetch instead of the public /api/shared/:token call.
  // For edit-shares we additionally render a banner pointing the user to
  // their main tree, where they can actually make changes (the viewer
  // itself is snapshot-based and read-only).
  return (
    <>
      {share?.permissions === "edit" && (
        <div className="mx-auto max-w-3xl px-4 pt-4 sm:px-6">
          <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
            <span className="text-base leading-none">✏️</span>
            <div className="flex-1">
              <div className="font-medium">You can edit this share.</div>
              <div className="text-xs text-emerald-800 dark:text-emerald-300">
                Edits go through your{" "}
                <Link
                  to="/"
                  className="underline hover:text-emerald-700 dark:hover:text-emerald-200"
                >
                  main tree
                </Link>
                {" "}— look for{" "}
                <strong>{share.scopeTitle ?? "the shared group"}</strong> in
                your sidebar (it'll be tagged{" "}
                <span className="rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                  shared
                </span>
                ). This page is a read-only preview.
              </div>
            </div>
          </div>
        </div>
      )}
      {share && share.acceptedAt && (
        <div className="mx-auto mt-3 max-w-3xl px-4 sm:px-6">
          <ActivityWatchToggle
            scope={share.scope}
            scopeId={share.scopeId}
            accessToken={accessToken}
          />
        </div>
      )}
      <SharedView
        loaderKey={id}
        loader={async () => {
          const data = await fetchShareSnapshot(accessToken, id);
          return data as never;
        }}
      />
    </>
  );
}

// Recipients of a shared Excalidraw scene land here from the accepted-shares
// list, but the snapshot viewer can't render a drawing.  Send them to their
// Excalidraw workspace, where the scene syncs in and is fully interactive.
function ExcalidrawShareNotice({ share }: { share: ShareRow }) {
  const canEdit = share.permissions === "edit";
  return (
    <main className="mx-auto max-w-2xl px-6 py-8">
      <div className="mb-4 flex items-baseline justify-between">
        <h1 className="text-xl font-semibold tracking-tight">Shared drawing</h1>
        <Link
          to="/shared-with-me"
          className="text-sm text-slate-500 underline hover:text-slate-700"
        >
          Back
        </Link>
      </div>
      <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-4">
        <div className="flex items-start gap-3">
          <span aria-hidden className="text-2xl leading-none">
            🖼️
          </span>
          <div className="flex-1">
            <div className="font-medium text-indigo-950">
              “{share.scopeTitle ?? "Untitled drawing"}” is an Excalidraw
              drawing.
            </div>
            <div className="mt-0.5 text-[11px] text-indigo-700">
              from {ownerLabel(share)} ·{" "}
              {canEdit ? "you can edit" : "view only"}
            </div>
            <p className="mt-2 text-xs text-indigo-800">
              Drawings open in your Excalidraw workspace, not on this page. Open
              it below
              {canEdit
                ? " to draw on it live with the owner."
                : " to follow the owner's changes live."}
            </p>
            <Link
              to={`/?scene=${encodeURIComponent(share.scopeId)}`}
              className="mt-3 inline-block rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500"
            >
              Open drawing →
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}

function ownerLabel(s: ShareRow): string {
  if (!s.other) return "someone";
  if ("pending" in s.other) return s.other.email;
  if (s.other.handle) return `@${s.other.handle}`;
  return s.other.display_name?.trim() || s.other.email;
}
